import { SAVE_PREFIX, SETTINGS_KEY } from '@/core/config';
import { bus, Events } from '@/core/events';
import { DEFAULT_SETTINGS, type Settings } from '@/core/types';
import { migrate, type SaveData, type SaveSlot, SAVE_SLOTS } from './schema';

export interface Storage { getItem(k: string): string | null; setItem(k: string, v: string): void; removeItem(k: string): void }

/** localStorage-backed save store. Every method is total: failures log and return null/false. */
export class SaveManager {
  constructor(private readonly storage: Storage = SaveManager.defaultStorage(), private readonly disabled = false) {}

  static defaultStorage(): Storage {
    try {
      if (typeof localStorage !== 'undefined') return localStorage;
    } catch { /* private mode */ }
    const mem = new Map<string, string>();
    return { getItem: (k) => mem.get(k) ?? null, setItem: (k, v) => void mem.set(k, v), removeItem: (k) => void mem.delete(k) };
  }

  key(slot: SaveSlot): string { return SAVE_PREFIX + slot; }

  read(slot: SaveSlot): SaveData | null {
    try {
      const raw = this.storage.getItem(this.key(slot));
      if (!raw) return null;
      return migrate(JSON.parse(raw));
    } catch (e) {
      console.warn('[save] unreadable slot', slot, e);
      return null;
    }
  }

  write(slot: SaveSlot, data: SaveData): boolean {
    if (this.disabled) return false;
    try {
      this.storage.setItem(this.key(slot), JSON.stringify(data));
      bus.emitTyped(Events.SAVE_WRITTEN, { slot });
      return true;
    } catch (e) {
      console.warn('[save] write failed', slot, e);
      return false;
    }
  }

  remove(slot: SaveSlot): void { try { this.storage.removeItem(this.key(slot)); } catch { /* ignore */ } }
  clearAll(): void { for (const s of SAVE_SLOTS) this.remove(s); }
  hasAny(): boolean { return SAVE_SLOTS.some((s) => this.read(s) !== null); }
  /** Most recently updated slot, for "继续旅程". */
  latest(): { slot: SaveSlot; data: SaveData } | null {
    let best: { slot: SaveSlot; data: SaveData } | null = null;
    for (const slot of SAVE_SLOTS) {
      const data = this.read(slot);
      if (data && (!best || data.updatedAt > best.data.updatedAt)) best = { slot, data };
    }
    return best;
  }

  readSettings(): Settings {
    try {
      const raw = this.storage.getItem(SETTINGS_KEY);
      return raw ? { ...DEFAULT_SETTINGS, ...(JSON.parse(raw) as Partial<Settings>) } : { ...DEFAULT_SETTINGS };
    } catch { return { ...DEFAULT_SETTINGS }; }
  }
  writeSettings(s: Settings): void {
    try { this.storage.setItem(SETTINGS_KEY, JSON.stringify(s)); } catch { /* ignore */ }
  }
}
